import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'

@Injectable({
  providedIn: 'root'
})
export class HelperService {
  constructor(private http: HttpClient) {}

  uploadFile(body: any) {
    return this.http.post('Attachment/Upload', body)
  }

  downLoadFile(fileName: string) {
    return this.http.post('Attachment/Download', '', {
      params: { fileName: fileName },
      headers: {
        'Content-type': 'application/pdf'
      },
      responseType: 'arraybuffer'
    })
  }

  // Attachments live in a private blob container, so a stored reference can't be opened
  // directly. Stream it through the authorized backend endpoint and save it client-side.
  downloadAttachment(ref: string) {
    if (!ref) return
    const name = ref.split('/').pop()?.split('?')[0] || 'downloaded-file'
    this.downLoadFile(ref).subscribe((res: any) =>
      this.createBlob(res, name, this.contentTypeFor(name))
    )
  }

  contentTypeFor(name: string): string {
    const ext = name.split('.').pop()?.toLowerCase()
    const map: { [k: string]: string } = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      gif: 'image/gif'
    }
    return (ext && map[ext]) || 'application/octet-stream'
  }

  // will enhance later and use it in all components
  // createBlob(file:any, name:any) {
  //   var mediaType = 'application/pdf';
  //   // It is necessary to create a new blob object with mime-type explicitly set
  //   // otherwise only Chrome works like it should
  //   var newBlob = new Blob([file], { type: 'application/pdf' });

  //   // IE doesn't allow using a blob object directly as link href
  //   // instead it is necessary to use msSaveOrOpenBlob
  //   if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
  //     (window.navigator as any).msSaveOrOpenBlob(newBlob);
  //     return;
  //   }

  //   // For other browsers:
  //   // Create a link pointing to the ObjectURL containing the blob.
  //   const data = window.URL.createObjectURL(newBlob);

  //   var link = document.createElement('a');
  //   link.href = data;
  //   link.download = name;
  //   // this is necessary as link.click() does not work on the latest firefox
  //   link.dispatchEvent(
  //     new MouseEvent('click', {
  //       bubbles: true,
  //       cancelable: true,
  //       view: window,
  //     })
  //   );

  //   setTimeout(function () {
  //     // For Firefox it is necessary to delay revoking the ObjectURL
  //     window.URL.revokeObjectURL(data);
  //     link.remove();
  //   }, 100);
  // };

  createTemplate(tempNum: number) {
    return this.http.post('Attachment/createTemplate', '', {
      params: {
        templateEnum: tempNum
      },
      responseType: 'arraybuffer'
    })
  }

  importDocument(docNum: number, formData: any) {
    // Risk : 1 - Treatment : 2 - asset : 3
    return this.http.post('Attachment/Import', formData, {
      params: {
        templateEnum: docNum
      }
    })
  }

  exportDocument(docNum: number, id?: any) {
    // Risk : 1 - Treatment : 2 - asset : 3
    return this.http.post('Attachment/Export', '', {
      params: {
        templateEnum: docNum,
        id: id
      },
      responseType: 'arraybuffer'
    })
  }

  createBlob(response: any, fileName: string, responseFileType: string) {
    const file = new Blob([response], { type: responseFileType })
    const fileURL = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = fileURL
    a.target = '_blank'
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }

  getDashedDateWithoutTime(value: Date) {
    if (!value) return null
    const date = value.toLocaleDateString().split('/')
    const yyyy = date[2]
    const mm = date[0]
    const dd = date[1]
    const fullDate = new Date(yyyy + '-' + mm + '-' + dd)
    return new Date(
      fullDate.getTime() + Math.abs(fullDate.getTimezoneOffset() * 60000)
    )
      .toISOString()
      .split('T')[0]
  }

  formatNumberWithDigits(number: any, digits = 2): string | undefined {
    if (!number.toString()) return
    const num = +number
    return num.toLocaleString('en-US', {
      minimumIntegerDigits: digits,
      useGrouping: false
    })
  }

  getTimeFromDate(date: Date): string {
    const stringTimeFormat =
      this.formatNumberWithDigits(new Date(date).getHours()) +
      ':' +
      this.formatNumberWithDigits(new Date(date).getMinutes()) +
      ':00.000'
    return stringTimeFormat
  }

  // changeDateFormat(value: any): any {
  //   if (!value) return null;
  //   const date = value.toLocaleDateString().split('/');
  //   const yyyy = date[2];
  //   const mm = date[0];
  //   const dd = date[1];
  //   const fullDate = new Date(yyyy + '-' + mm + '-' + dd);
  //   return new Date(
  //     fullDate.getTime() + Math.abs(fullDate.getTimezoneOffset() * 60000)
  //   ).toISOString();
  // }

  changeDateFormat(value: any): string | null {
    if (!value) return null

    const date = new Date(value)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date passed to changeDateFormat:', value)
      return null
    }

    // Convert to UTC ISO string (removing timezone offset)
    return new Date(
      date.getTime() + Math.abs(date.getTimezoneOffset() * 60000)
    ).toISOString()
  }
  normalizeArabic(text: string): string {
    if (!text) return ''

    return text
      .replace(/[\u064B-\u065F]/g, '') // Remove tashkeel (diacritics)
      .replace(/[إأآا]/g, 'ا') // Normalize Alif
      .replace(/ى/g, 'ي') // Normalize Alef Maqsura to Yeh
      .replace(/ئ/g, 'ي') // Normalize Yeh Hamza to Yeh
      .replace(/ؤ/g, 'و') // Normalize Waw Hamza to Waw
      .replace(/ة/g, 'ه') // Normalize Ta Marbuta to Ha
      .replace(/\s+/g, ' ') // Remove extra spaces
      .trim() // Trim leading/trailing spaces
      .toLowerCase() // Normalize case
  }
  base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64)
    const byteArrays = []
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512)
      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }
      byteArrays.push(new Uint8Array(byteNumbers))
    }
    return new Blob(byteArrays, { type: contentType })
  }
}
